// src/pages/Settings.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const ROLE_LABELS = { admin: 'Administrador', director: 'Director', supervisor: 'Supervisor', manager: 'Gerente RRHH' };

const PERMISSION_LABELS = {
  canViewSchedules: 'Ver horarios', canEditSchedules: 'Editar horarios', canDeleteSchedules: 'Eliminar turnos',
  canViewEmployees: 'Ver empleados', canEditEmployees: 'Editar empleados', canDownloadWorkPlans: 'Descargar planes de trabajo',
  canViewCitas: 'Ver citas', canEditCitas: 'Editar citas', canManageRoles: 'Gestionar roles y permisos', canApproveChanges: 'Aprobar cambios de horario',
    canViewAsistencia: 'Ver Dashboard de Asistencia', canManageDevices: 'Gestionar Dispositivos',
};

function formatShift12h(shift) {
  if (!shift) return '';
  if (shift.includes('AM') || shift.includes('PM')) return shift.replace('/', ' - ');
  let start = '', end = '';
  if (shift.includes('/')) { [start, end] = shift.split('/'); }
  else if (shift.includes('-')) { [start, end] = shift.split('-'); }
  else return shift;
  const convert = (time) => {
    if (!time) return '';
    let [h, m] = time.split(':');
    h = parseInt(h, 10);
    m = m ? String(m).padStart(2,'0') : '00';
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return h + ':' + m + ' ' + ampm;
  };
  return convert(start) + ' - ' + convert(end);
}

export default function Settings() {
  const { user, can, DEFAULT_PERMISSIONS, getPermissions, updatePermissions } = useAuth();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState({});
  const [saved, setSaved] = useState(false);
  const [activeTab, setActiveTab] = useState('permissions');
  const [approvals, setApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);

  useEffect(() => {
    if (!user || !can('canManageRoles')) { navigate('/dashboard'); return; }
    const loaded = {};
    Object.keys(ROLE_LABELS).forEach(role => { loaded[role] = { ...getPermissions(role) }; });
    setPermissions(loaded);
  }, [user]);

  // Carga solicitudes desde Supabase via dynamic import para evitar deps circulares
  const loadApprovals = useCallback(async () => {
    setLoadingApprovals(true);
    try {
      const { supabase } = await import('../supabaseClient');
      const { data, error } = await supabase.from('approval_requests').select('*').order('created_at', { ascending: false });
      if (!error && data) setApprovals(Array.isArray(data) ? data : []);
    } catch(e) { console.error('Error cargando solicitudes:', e); setApprovals([]); }
    finally { setLoadingApprovals(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'approvals') loadApprovals();
  }, [activeTab, loadApprovals]);

  // Revisa solicitudes nuevas cada 12 horas mientras el tab de solicitudes esta activo
  useEffect(() => {
    if (activeTab !== 'approvals') return;
    const interval = setInterval(loadApprovals, 12 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [activeTab, loadApprovals]);

  const handleToggleAndSave = (role, perm) => {
    if (role === 'admin') return;
    const updated = { ...permissions[role], [perm]: !permissions[role]?.[perm] };
    setPermissions(prev => ({ ...prev, [role]: updated }));
    updatePermissions(role, updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  const handleReset = () => {
    const reset = {};
    Object.keys(ROLE_LABELS).forEach(role => { reset[role] = { ...DEFAULT_PERMISSIONS[role] }; });
    setPermissions(reset);
    Object.keys(ROLE_LABELS).forEach(role => { if (role !== 'admin') updatePermissions(role, DEFAULT_PERMISSIONS[role]); });
    setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  const handleApproval = async (id, status) => {
    try {
      const { supabase } = await import('../supabaseClient');
      await supabase.from('approval_requests').update({ status, resolved_at: new Date().toISOString(), resolved_by: user.name }).eq('id', id);
      loadApprovals();
    } catch(e) { console.error(e); }
  };

  const safeApprovals = Array.isArray(approvals) ? approvals : [];
  const pendingApprovals = safeApprovals.filter(a => a.status === 'pending');
  const resolvedApprovals = safeApprovals.filter(a => a.status !== 'pending');

  if (!user || !can('canManageRoles')) return null;

  return (
    <div style={{ color: '#fff' }}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem' }}>
        <div>
          <h1 style={{ fontSize:'1.6rem',fontWeight:800,color:'#F5C518',margin:0 }}>Configuracion</h1>
          <p style={{ color:'#666',margin:'4px 0 0',fontSize:14 }}>Gestiona roles, permisos y solicitudes</p>
          {saved && <p style={{ color:'#34d399',margin:'4px 0 0',fontSize:12 }}>✓ Cambios guardados</p>}
        </div>
        {pendingApprovals.length > 0 && (
          <div style={{ background:'rgba(239,68,68,0.15)',border:'1px solid rgba(239,68,68,0.4)',borderRadius:8,padding:'8px 16px',color:'#f87171',fontWeight:700,fontSize:14,cursor:'pointer' }} onClick={()=>setActiveTab('approvals')}>
            🔔 {pendingApprovals.length} solicitud(es) pendiente(s)
          </div>
        )}
      </div>

      <div style={{ display:'flex',gap:8,marginBottom:24,borderBottom:'1px solid #222' }}>
        {[{ key:'permissions', label:'Permisos por Rol' }, { key:'approvals', label:'Solicitudes' + (pendingApprovals.length > 0 ? ' (' + pendingApprovals.length + ')' : '') }].map(tab => (
          <button key={tab.key} onClick={()=>setActiveTab(tab.key)} style={{ background:'none',border:'none',borderBottom:activeTab===tab.key?'2px solid #F5C518':'2px solid transparent',color:activeTab===tab.key?'#F5C518':'#666',cursor:'pointer',padding:'10px 20px',fontSize:14,fontWeight:activeTab===tab.key?700:400,marginBottom:-1 }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'permissions' && (
        <div>
          <div style={{ background:'rgba(245,197,24,0.05)',border:'1px solid rgba(245,197,24,0.15)',borderRadius:8,padding:'12px 16px',marginBottom:24,fontSize:13,color:'#888' }}>
            Los cambios se aplican <strong style={{ color:'#F5C518' }}>inmediatamente</strong>. Administrador siempre tiene acceso completo.
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%',borderCollapse:'collapse',minWidth:700 }}>
              <thead>
                <tr>
                  <th style={{ textAlign:'left',padding:'10px 16px',color:'#666',fontSize:12,borderBottom:'1px solid #222',minWidth:200 }}>Permiso</th>
                  {Object.keys(ROLE_LABELS).map(role => (
                    <th key={role} style={{ textAlign:'center',padding:'10px 16px',color:role==='admin'?'#F5C518':'#aaa',fontSize:12,borderBottom:'1px solid #222',minWidth:120 }}>
                      {ROLE_LABELS[role]}{role==='admin'&&<div style={{ fontSize:10,color:'#555' }}>Siempre activo</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(PERMISSION_LABELS).map(([perm, label]) => (
                  <tr key={perm} style={{ borderBottom:'1px solid #1a1a1a' }}>
                    <td style={{ padding:'10px 16px',fontSize:13,color:'#ccc' }}>{label}</td>
                    {Object.keys(ROLE_LABELS).map(role => {
                      const isAdmin = role === 'admin';
                      const val = isAdmin ? true : (permissions[role]?.[perm] ?? false);
                      return (
                        <td key={role} style={{ textAlign:'center',padding:'10px 16px' }}>
                          <button onClick={() => handleToggleAndSave(role, perm)} title={isAdmin?'Siempre activo':(val?'Desactivar':'Activar')} style={{ width:48,height:26,borderRadius:13,border:'none',background:val?'#F5C518':'#333',cursor:isAdmin?'not-allowed':'pointer',position:'relative',transition:'background 0.2s',opacity:isAdmin?0.6:1 }}>
                            <span style={{ position:'absolute',top:3,left:val?25:3,width:20,height:20,borderRadius:'50%',background:'#fff',transition:'left 0.2s',display:'block' }} />
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display:'flex',gap:12,marginTop:24 }}>
            <button onClick={handleReset} style={{ padding:'10px 24px',background:'rgba(107,114,128,0.15)',border:'1px solid #444',borderRadius:8,color:'#aaa',fontWeight:600,cursor:'pointer',fontSize:14 }}>Restablecer predeterminados</button>
          </div>
        </div>
      )}

      {activeTab === 'approvals' && (
        <div>
          <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16 }}>
            <span style={{ color:'#888',fontSize:13 }}>Solicitudes sincronizadas desde Supabase — visibles desde cualquier dispositivo.</span>
            <button onClick={loadApprovals} style={{ padding:'6px 14px',background:'#1a1a1a',border:'1px solid #333',borderRadius:6,color:'#aaa',cursor:'pointer',fontSize:12 }}>{loadingApprovals?'Cargando...':'↻ Actualizar'}</button>
          </div>
          {!loadingApprovals && pendingApprovals.length === 0 && resolvedApprovals.length === 0 && (
            <div style={{ textAlign:'center',padding:'3rem',color:'#555' }}>No hay solicitudes de aprobacion</div>
          )}
          {pendingApprovals.length > 0 && (
            <div style={{ marginBottom:32 }}>
              <h3 style={{ color:'#fbbf24',fontSize:14,fontWeight:700,marginBottom:12 }}>PENDIENTES ({pendingApprovals.length})</h3>
              <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
                {pendingApprovals.map(req => (
                  <div key={req.id} style={{ background:'#1a1a1a',border:'1px solid rgba(251,191,36,0.3)',borderRadius:10,padding:'16px' }}>
                    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12 }}>
                      <div>
                        <div style={{ fontWeight:700,color:'#fff',marginBottom:4 }}>{req.action||req.type}</div>
                        <div style={{ color:'#888',fontSize:13,marginBottom:2 }}>Por: <span style={{ color:'#aaa' }}>{req.requested_by}</span></div>
                        {req.employee_name&&<div style={{ color:'#888',fontSize:13,marginBottom:2 }}>Empleado: <span style={{ color:'#aaa' }}>{req.employee_name}</span></div>}
                        {req.date&&<div style={{ color:'#888',fontSize:13,marginBottom:2 }}>Fecha: <span style={{ color:'#aaa' }}>{req.date}</span></div>}
                        {req.shift&&<div style={{color:'#888',fontSize:13,marginBottom:2}}>Turno: <span style={{color:'#aaa'}}>{formatShift12h(req.shift)}</span></div>}
                        {req.notes&&<div style={{ color:'#888',fontSize:13 }}>Notas: <span style={{ color:'#aaa' }}>{req.notes}</span></div>}
                        <div style={{ color:'#555',fontSize:11,marginTop:6 }}>{req.created_at ? new Date(req.created_at).toLocaleString('es-PR', { hour12: true }) : ''}</div>
                      </div>
                      <div style={{ display:'flex',gap:8,flexShrink:0 }}>
                        <button onClick={()=>handleApproval(req.id,'approved')} style={{ padding:'8px 16px',background:'rgba(52,211,153,0.15)',border:'1px solid rgba(52,211,153,0.4)',borderRadius:6,color:'#34d399',cursor:'pointer',fontWeight:700,fontSize:13 }}>✓ Aprobar</button>
                        <button onClick={()=>handleApproval(req.id,'rejected')} style={{ padding:'8px 16px',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:6,color:'#f87171',cursor:'pointer',fontWeight:700,fontSize:13 }}>✗ Rechazar</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {resolvedApprovals.length > 0 && (
            <div>
              <h3 style={{ color:'#666',fontSize:14,fontWeight:700,marginBottom:12 }}>HISTORIAL ({resolvedApprovals.length})</h3>
              <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                {resolvedApprovals.map(req => (
                  <div key={req.id} style={{ background:'#111',border:'1px solid #222',borderRadius:10,padding:'12px 16px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8 }}>
                    <span style={{ color:'#666',fontSize:13 }}>{req.action||req.type}{req.employee_name?' - '+req.employee_name:''}{req.date?' ('+req.date+')':''} <span style={{ color:'#555',fontSize:11 }}>por {req.requested_by}</span></span>
                    <span style={{ padding:'3px 10px',borderRadius:20,fontSize:12,fontWeight:700,background:req.status==='approved'?'rgba(52,211,153,0.1)':'rgba(239,68,68,0.1)',color:req.status==='approved'?'#34d399':'#f87171' }}>
                      {req.status==='approved'?'✓ Aprobado':'✗ Rechazado'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
